/**
 * The one way the studio talks to the control plane.
 *
 * There used to be five of these. `api.ts` had the real one; `CommentThread`,
 * `RunTimeline`, `FleetView`, `KnowledgeView` and `ArtifactsView` each had a
 * shorter copy, written when a screen needed an endpoint the shared client did
 * not expose. The copies were not equivalent, and the differences were the
 * kind nobody notices until it matters:
 *
 * - **None of them handled 401.** A session that expired while a screen was
 *   open showed a raw error instead of signing the reader back in.
 * - **Each unwrapped errors differently.** One read `message`, one read
 *   `message ?? error`, one read `message ?? reason`. A refusal the server
 *   explained in the field a given copy did not read surfaced as
 *   "403 Forbidden" — which is exactly the wording the shared error handler on
 *   the Worker exists to replace.
 * - **None knew the typed refusals.** A 428 carrying the connections a run
 *   needs, or a 409 from a lost editor race, arrived as a bare `Error`.
 *
 * One transport, so a screen added tomorrow gets all of that by default rather
 * than by remembering.
 */
import type { MissingConnection } from '@reef/api-contract';

export class ConnectionsRequiredError extends Error {
  constructor(readonly missing: MissingConnection[]) {
    super('Some connections need to be authorized before this can run.');
    this.name = 'ConnectionsRequiredError';
  }
}

/**
 * The deployment's own ceiling, not the workflow's.
 *
 * Kept separate from a generic error because the remedy is different and only
 * an admin has it: waiting will clear a concurrency ceiling, and nothing the
 * caller does will clear a spend one.
 */
export class DeploymentAtCapacityError extends Error {
  constructor(
    message: string,
    readonly ceiling: string,
  ) {
    super(message);
    this.name = 'DeploymentAtCapacityError';
  }
}

export class StaleEditorError extends Error {
  constructor(readonly path: string) {
    super('Someone else saved this workflow while you were editing.');
    this.name = 'StaleEditorError';
  }
}

/**
 * Start the Okta sign-in — at most once per page.
 *
 * better-auth's generic-OAuth sign-in is a `POST` that *returns* the provider
 * URL — navigating to it directly is a 404, and the redirect it would have
 * issued is also what sets the state cookie the callback later validates. So
 * the browser has to make the call and then follow the URL it hands back.
 *
 * **Which is why this deduplicates.** Each call mints a fresh OAuth state and
 * sets `better-auth.state` to it, so a second call overwrites the first's
 * cookie — and then the browser follows whichever `location` was assigned last,
 * which need not be the one the surviving cookie names. better-auth correctly
 * refuses that as `state_mismatch`, and the symptom is an error page nobody can
 * reproduce by hand.
 *
 * Two callers race here routinely, and neither is a mistake: React's
 * `StrictMode` invokes the session effect twice in development, and a cold page
 * load fires four control-plane requests at once that all come back 401. So the
 * promise is memoized at module scope rather than guarded per component —
 * signing in is a one-way door, and starting it twice is never right.
 */
let signInStarted: Promise<never> | null = null;

export function startOktaSignIn(callbackURL = '/'): Promise<never> {
  // Released on failure, so a misconfigured provider is a retryable error
  // rather than a page that has to be reloaded before it will try again.
  signInStarted ??= beginOktaSignIn(callbackURL).catch((error) => {
    signInStarted = null;
    throw error;
  }) as Promise<never>;
  return signInStarted;
}

async function beginOktaSignIn(callbackURL: string): Promise<never> {
  const response = await fetch('/api/auth/sign-in/oauth2', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      providerId: 'okta',
      // Absolute, because the Worker and the builder are different origins — a
      // bare path would resolve against the Worker, which serves no UI.
      callbackURL: new URL(callbackURL, window.location.origin).href,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Could not start the Okta sign-in (${response.status}). Check OKTA_ISSUER, OKTA_CLIENT_ID and OKTA_CLIENT_SECRET.`,
    );
  }

  const { url } = (await response.json()) as { url?: string };
  if (!url) throw new Error('Okta sign-in did not return a redirect URL.');

  window.location.href = url;
  // Navigation is asynchronous; block the caller from continuing meanwhile.
  return new Promise<never>(() => {});
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'content-type': 'application/json', ...init.headers },
  });

  if (response.ok) return (await response.json()) as T;

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (response.status === 401) {
    // Deduplicated inside, so the four requests a cold page load fires start
    // one sign-in between them. The catch is here because this caller throws
    // its own error and never awaits the redirect.
    void startOktaSignIn(window.location.pathname).catch(() => {});
    throw new Error('redirecting to sign in');
  }
  if (response.status === 428 && Array.isArray(body.missing)) {
    throw new ConnectionsRequiredError(body.missing as MissingConnection[]);
  }
  /*
   * 409 means "the thing you are acting on moved on", and reef has more than
   * one of those. A stale editor is one; an action that was already decided,
   * or applied and then failed at the server, is another — and reporting those
   * as "someone else saved this workflow while you were editing" told an
   * approver a confidently wrong story about what had happened. So this
   * narrows on the error code the control plane sends rather than on the
   * status alone, and anything else falls through to its own message.
   */
  if (response.status === 409 && body.error === 'stale_editor') {
    throw new StaleEditorError(String(body.path ?? ''));
  }
  if (response.status === 429) {
    throw new DeploymentAtCapacityError(
      String(body.message ?? 'This deployment is at its run ceiling.'),
      String(body.ceiling ?? 'unknown'),
    );
  }

  /*
   * `reason` as well as `message`, because the two are used for different
   * shapes and both are the server explaining itself. An endpoint that refuses
   * a decision — "not applied: the connection is not authorized for you" — puts
   * it in `reason`, and dropping it left the UI showing "409 Conflict" for a
   * failure that had a perfectly good sentence attached.
   */
  const explanation = [body.message, body.reason].find((value) => typeof value === 'string');
  throw new Error(explanation ?? `${response.status} ${response.statusText}`);
}
